import * as React from 'react';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceNotice,
  workspaceInputClass,
} from '@/components/ui/workspace';
import type { ClientDetail, UpdateClientInput } from '@/lib/api/clients';
import { cn } from '@/lib/utils/cn';

/**
 * Edit a client (T-0227 / C2-10): name, main contact and notes.
 *
 * Presentational, like the delete-engagement dialog: the page owns the
 * request (`onSave`), the pending state and the server's refusal. Only the
 * fields that changed are sent, so the audit entry names what the user
 * actually edited and an unchanged name never re-runs the vendor
 * knowledge base rescan. Problems with a value show under its field as
 * soon as the field is left or the save is tried; the save button stays
 * enabled so the dialog says what is wrong instead of refusing silently.
 */

export interface EditClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Pick<ClientDetail, 'name' | 'primary_contact_name' | 'primary_contact_email' | 'notes'>;
  saving: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  /** Called with the changed fields only; not called when nothing changed. */
  onSave: (changes: UpdateClientInput) => void;
}

// The same syntactic check the API applies (`EMAIL_RE` in services/clients.ts).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Draft {
  name: string;
  primary_contact_name: string;
  primary_contact_email: string;
  notes: string;
}

type Problems = Partial<Record<keyof Draft, string>>;

/** The inline messages for a draft; empty when it can be saved. */
export function clientDraftProblems(draft: Draft): Problems {
  const problems: Problems = {};
  if (draft.name.trim().length === 0) problems.name = 'Client name is required.';
  else if (draft.name.trim().length > 200)
    problems.name = 'Client name must be 200 characters or fewer.';
  if (draft.primary_contact_name.trim().length === 0) {
    problems.primary_contact_name = 'Primary contact is required.';
  }
  if (!EMAIL_RE.test(draft.primary_contact_email.trim())) {
    problems.primary_contact_email = 'Enter a valid email.';
  }
  return problems;
}

/** What changed between the stored client and the draft, trimmed as the server stores it. */
export function clientDraftChanges(
  client: EditClientDialogProps['client'],
  draft: Draft,
): UpdateClientInput {
  const changes: UpdateClientInput = {};
  if (draft.name.trim() !== client.name) changes.name = draft.name.trim();
  if (draft.primary_contact_name.trim() !== client.primary_contact_name) {
    changes.primary_contact_name = draft.primary_contact_name.trim();
  }
  if (draft.primary_contact_email.trim() !== client.primary_contact_email) {
    changes.primary_contact_email = draft.primary_contact_email.trim();
  }
  const notes = draft.notes.trim();
  if (notes !== (client.notes ?? '')) changes.notes = notes.length === 0 ? null : notes;
  return changes;
}

function draftOf(client: EditClientDialogProps['client']): Draft {
  return {
    name: client.name,
    primary_contact_name: client.primary_contact_name,
    primary_contact_email: client.primary_contact_email,
    notes: client.notes ?? '',
  };
}

export function EditClientDialog({
  open,
  onOpenChange,
  client,
  saving,
  error,
  onSave,
}: EditClientDialogProps) {
  const [draft, setDraft] = React.useState<Draft>(() => draftOf(client));
  const [touched, setTouched] = React.useState<Partial<Record<keyof Draft, boolean>>>({});
  const [tried, setTried] = React.useState(false);

  // Start from the stored values every time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setDraft(draftOf(client));
      setTouched({});
      setTried(false);
    }
  }, [open, client]);

  const problems = clientDraftProblems(draft);
  const shown = (field: keyof Draft) => (tried || touched[field] ? problems[field] : undefined);
  const set =
    (field: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDraft((d) => ({ ...d, [field]: e.target.value }));
  const touch = (field: keyof Draft) => () => setTouched((t) => ({ ...t, [field]: true }));

  // Save from the footer button or Enter in a field: the same path.
  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setTried(true);
    if (Object.keys(problems).length > 0 || saving) return;
    const changes = clientDraftChanges(client, draft);
    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }
    onSave(changes);
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={(next) => {
        if (saving) return;
        onOpenChange(next);
      }}
      title="Edit client"
      description="Name, main contact and notes. Engagements and the team are managed on the client page."
      actions={
        <>
          <WorkspaceButton variant="tertiary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton variant="primary" onClick={() => submit()} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </WorkspaceButton>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-3.5">
        <WorkspaceField label="Client name" error={shown('name') ?? null}>
          {(id, describedBy) => (
            <WorkspaceInput
              id={id}
              value={draft.name}
              onChange={set('name')}
              onBlur={touch('name')}
              aria-describedby={describedBy}
              aria-invalid={shown('name') ? true : undefined}
              autoFocus
              disabled={saving}
            />
          )}
        </WorkspaceField>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <WorkspaceField label="Primary contact" error={shown('primary_contact_name') ?? null}>
            {(id, describedBy) => (
              <WorkspaceInput
                id={id}
                value={draft.primary_contact_name}
                onChange={set('primary_contact_name')}
                onBlur={touch('primary_contact_name')}
                aria-describedby={describedBy}
                aria-invalid={shown('primary_contact_name') ? true : undefined}
                disabled={saving}
              />
            )}
          </WorkspaceField>
          <WorkspaceField label="Contact email" error={shown('primary_contact_email') ?? null}>
            {(id, describedBy) => (
              <WorkspaceInput
                id={id}
                type="email"
                value={draft.primary_contact_email}
                onChange={set('primary_contact_email')}
                onBlur={touch('primary_contact_email')}
                aria-describedby={describedBy}
                aria-invalid={shown('primary_contact_email') ? true : undefined}
                disabled={saving}
              />
            )}
          </WorkspaceField>
        </div>
        <WorkspaceField label="Notes" help="Optional. Shown on the client page to the firm team.">
          {(id, describedBy) => (
            <textarea
              id={id}
              value={draft.notes}
              onChange={set('notes')}
              aria-describedby={describedBy}
              rows={4}
              maxLength={4000}
              disabled={saving}
              className={cn(workspaceInputClass, 'h-auto resize-y py-2 leading-5')}
            />
          )}
        </WorkspaceField>
        {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
      </form>
    </WorkspaceDialog>
  );
}
